"use client";

import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api-client";
import type { Product } from "@/lib/api-client";
import { ProductCard } from "@/components/booking/product-card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Search, SlidersHorizontal, Home, CalendarDays, X } from "lucide-react";
import { useMemo, useState } from "react";
import { PROPERTY_TYPES, propertyTypeLabel, pluralPropertyLabel } from "@/components/booking/format";
import { useAppStore } from "@/lib/store";
import { useI18n } from "@/lib/i18n/provider";

export function BrowseView() {
  const user = useAppStore((s) => s.user);
  const { t, locale } = useI18n();
  const [q, setQ] = useState("");
  const [type, setType] = useState<string>("ALL");
  const [guests, setGuests] = useState<string>("");
  const [from, setFrom] = useState<string>("");
  const [to, setTo] = useState<string>("");
  const [showFilters, setShowFilters] = useState(false);
  const [submitted, setSubmitted] = useState({ q: "", type: "ALL", guests: "", from: "", to: "" });

  const queryKey = useMemo(() => ["products", submitted] as const, [submitted]);

  const params = new URLSearchParams();
  if (submitted.q) params.set("q", submitted.q);
  if (submitted.type !== "ALL") params.set("type", submitted.type);
  if (submitted.guests) params.set("guests", submitted.guests);
  if (submitted.from) params.set("from", submitted.from);
  if (submitted.to) params.set("to", submitted.to);
  const path = `/api/products${params.toString() ? `?${params}` : ""}`;

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => apiGet<{ products: Product[] }>(path),
  });

  const products = data?.products ?? [];

  const apply = () => setSubmitted({ q, type, guests, from, to });
  const reset = () => {
    setQ("");
    setType("ALL");
    setGuests("");
    setFrom("");
    setTo("");
    setSubmitted({ q: "", type: "ALL", guests: "", from: "", to: "" });
  };

  const hasFilters =
    submitted.q || submitted.type !== "ALL" || submitted.guests || submitted.from || submitted.to;

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
      {/* Hero */}
      <section className="mb-8 overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-600 to-teal-700 p-8 text-white sm:p-12">
        <div className="max-w-2xl">
          <Badge className="mb-3 bg-white/15 text-white border-white/20">
            {t("app.tagline")}
          </Badge>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
            {t("browse.heroTitle")}
          </h1>
          <p className="mt-3 max-w-xl text-white/85">{t("browse.heroSubtitle")}</p>
        </div>
      </section>

      {/* Search bar */}
      <section className="mb-6">
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && apply()}
              placeholder={t("browse.searchPlaceholder")}
              className="pl-9"
            />
          </div>
          <Button variant="outline" onClick={() => setShowFilters((v) => !v)} className="gap-2">
            <SlidersHorizontal className="h-4 w-4" />
            {t("common.filters")}
          </Button>
          <Button onClick={apply} className="gap-2">
            <Search className="h-4 w-4" />
            {t("common.search")}
          </Button>
        </div>

        {showFilters && (
          <div className="mt-4 grid gap-4 rounded-xl border bg-card p-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="grid gap-1.5">
              <Label>{t("filter.propertyType")}</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger>
                  <SelectValue placeholder={t("common.allTypes")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">{t("common.allTypes")}</SelectItem>
                  {PROPERTY_TYPES.map((ty) => (
                    <SelectItem key={ty} value={ty}>
                      {propertyTypeLabel(ty, locale)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label>{t("filter.guests")}</Label>
              <Input
                type="number"
                min={1}
                value={guests}
                onChange={(e) => setGuests(e.target.value)}
                placeholder={t("common.any")}
              />
            </div>
            <div className="grid gap-1.5">
              <Label>{t("filter.checkIn")}</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label>{t("filter.checkOut")}</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
          </div>
        )}

        {hasFilters ? (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
            <span className="text-muted-foreground">{t("browse.activeFilters")}</span>
            {submitted.q && <Chip>{submitted.q}</Chip>}
            {submitted.type !== "ALL" && (
              <Chip>{propertyTypeLabel(submitted.type, locale)}</Chip>
            )}
            {submitted.guests && (
              <Chip>{t("browse.guestsChip", { count: submitted.guests })}</Chip>
            )}
            {submitted.from && submitted.to && (
              <Chip>
                <CalendarDays className="mr-1 h-3 w-3" />
                {submitted.from} → {submitted.to}
              </Chip>
            )}
            <Button variant="ghost" size="sm" onClick={reset} className="h-7 gap-1">
              <X className="h-3 w-3" /> {t("common.clearAll")}
            </Button>
          </div>
        ) : null}
      </section>

      {/* Results */}
      <section>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold">
            {isLoading
              ? t("browse.loadingResults")
              : t("browse.resultsCount", {
                  count: products.length,
                  plural: pluralPropertyLabel(products.length, locale),
                })}
          </h2>
          {!user && <p className="text-sm text-muted-foreground">{t("browse.signInToReserve")}</p>}
        </div>

        {isLoading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="aspect-[4/3] w-full rounded-xl" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="grid place-items-center rounded-xl border border-dashed py-20 text-center">
            <Home className="mb-3 h-10 w-10 text-muted-foreground" />
            <p className="font-medium">{t("browse.noResultsTitle")}</p>
            <p className="text-sm text-muted-foreground">{t("browse.noResultsBody")}</p>
            {hasFilters && (
              <Button variant="outline" className="mt-4" onClick={reset}>
                {t("common.clearAll")}
              </Button>
            )}
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {products.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full border bg-muted px-3 py-1 text-xs font-medium">
      {children}
    </span>
  );
}
